import { exportIcs, parseIcs, assembleImport } from "@app/core";
import type { IcsExportInput, NamedRef } from "@app/core";
import { bulkUpsert } from "@app/storage";
import { create } from "zustand";
import { getServices } from "../platform.js";
import { saveTextFile, openTextFiles, type FileKind } from "../lib/file-io.js";
import { rehydrateStores } from "../lib/snapshot.js";
import { tr } from "../lib/tr.js";
import { useLabelsStore } from "./labels.store.js";
import { useTopicsStore } from "./topics.store.js";
import { useCalendarsStore } from "./calendars.store.js";
import { useIcsImportStore } from "./icsImport.store.js";
import { toast } from "./toast.store.js";

const ICS_KIND: FileKind = { name: "iCalendar", extensions: ["ics"], mime: "text/calendar" };

interface IcsState {
  busy: boolean;
  /** Export to .ics. Pass calendar ids to limit the export; omit/empty = all. */
  exportIcs: (calendarIds?: string[]) => Promise<void>;
  importIcs: () => Promise<void>;
}

export const useIcsStore = create<IcsState>((set) => ({
  busy: false,

  async exportIcs(calendarIds) {
    set({ busy: true });
    try {
      const { tasks, recurrence, recurrenceOverrides } = getServices();
      const all = await tasks.listActive();
      const pick = calendarIds && calendarIds.length ? new Set(calendarIds) : null;
      const selected = pick ? all.filter((t) => t.calendar_id && pick.has(t.calendar_id)) : all;
      if (!selected.length) {
        toast.info(tr("toast.ics.nothingToExport"));
        return;
      }
      const input: IcsExportInput = {
        tasks: selected,
        rulesById: byId(await recurrence.listActive()),
        overrides: await recurrenceOverrides.listAllActive(),
        labelsById: namedById(useLabelsStore.getState().labels),
        calendarsById: namedById(useCalendarsStore.getState().calendars),
        topicsById: namedById(useTopicsStore.getState().topics),
        now: new Date(),
      };
      const today = new Date().toISOString().slice(0, 10);
      await saveTextFile(exportIcs(input), `reminders-${today}.ics`, ICS_KIND);
      toast.success(tr("toast.ics.exported", { count: selected.length, noun: itemNoun(selected.length) }));
    } catch (e) {
      toast.error(tr("toast.ics.exportFailed", { error: msg(e) }));
    } finally {
      set({ busy: false });
    }
  },

  async importIcs() {
    const texts = await openTextFiles(ICS_KIND);
    if (texts === null) return; // cancelled

    const parsedList = texts.map(parseIcs).filter((p) => p.items.length > 0);
    if (!parsedList.length) {
      toast.info(tr("toast.ics.noneImportable"));
      return;
    }

    let totalImported = 0;
    let totalSkipped = 0;

    try {
      for (const parsed of parsedList) {
        const items = parsed.items;
        const today = new Date().toISOString().slice(0, 10);
        const suggestedName = parsed.calendarName ?? `Imported ${today}`;
        const target = await useIcsImportStore.getState().ask({
          count: items.length,
          fileCount: 1,
          suggestedName,
          calendars: useCalendarsStore.getState().calendars,
        });
        if (target.kind === "cancel") return;

        set({ busy: true });
        const calendarId =
          target.kind === "existing" ? target.calendarId : (await useCalendarsStore.getState().add(target.name)).id;
        const labelMap = await ensureLabels(items.flatMap((i) => i.categories));
        const topicMap = await ensureTopics(items.flatMap((i) => i.topics));

        const assembly = assembleImport(items, {
          resolveLabelIds: (names) => names.map((n) => labelMap.get(n)).filter(Boolean) as string[],
          resolveTopicIds: (names) => names.map((n) => topicMap.get(n)).filter(Boolean) as string[],
          // The chosen target calendar is authoritative for every imported item.
          resolveCalendarId: () => calendarId,
          existingUids: await existingUids(),
        });

        // One native batch per file — not one IPC write per imported event —
        // and yielding between batches so a 10k-event file does not freeze the
        // main thread in one block (same contract as a backup restore).
        await bulkUpsert(getServices().db, {
          recurrenceRules: assembly.rules,
          tasks: assembly.tasks,
          recurrenceOverrides: assembly.overrides,
        }, { yieldBetweenBatches: true });

        totalImported += assembly.tasks.length;
        totalSkipped += assembly.skipped || 0;
      }
    } catch (e) {
      toast.error(tr("toast.ics.importFailed", { error: msg(e) }));
    } finally {
      set({ busy: false });
      if (totalImported > 0 || totalSkipped > 0) {
        await rehydrateStores();
        const vars = { count: totalImported, noun: itemNoun(totalImported), skipped: totalSkipped };
        toast.success(tr(totalSkipped ? "toast.ics.importedSkipped" : "toast.ics.imported", vars));
      }
    }
  },
}));

/** UIDs already present locally (for dedup). Only tasks that carry an ical_uid. */
async function existingUids(): Promise<Set<string>> {
  const tasks = await getServices().tasks.listActive();
  return new Set(tasks.map((t) => t.ical_uid).filter(Boolean) as string[]);
}

// Match existing labels/topics by NORMALIZED name (trim + case-fold) so an
// imported "Work"/"work"/" Work " reuses the one topic instead of doubling it.
// Returned map is keyed by the RAW incoming name (what the resolver looks up).
async function ensureLabels(names: string[]): Promise<Map<string, string>> {
  const store = useLabelsStore.getState();
  const byKey = new Map(store.labels.map((l) => [norm(l.name), l.id]));
  const map = new Map<string, string>();
  for (const name of unique(names)) {
    let id = byKey.get(norm(name));
    if (!id) {
      id = (await store.add(name)).id;
      byKey.set(norm(name), id);
    }
    map.set(name, id);
  }
  return map;
}

async function ensureTopics(names: string[]): Promise<Map<string, string>> {
  const store = useTopicsStore.getState();
  const byKey = new Map(store.topics.map((t) => [norm(t.name), t.id]));
  const map = new Map<string, string>();
  for (const name of unique(names)) {
    let id = byKey.get(norm(name));
    if (!id) {
      id = (await store.add(name)).id;
      byKey.set(norm(name), id);
    }
    map.set(name, id);
  }
  return map;
}

function norm(s: string): string {
  return s.trim().toLowerCase();
}

function unique(xs: string[]): string[] {
  return [...new Set(xs.filter((x) => x && x.trim()))];
}

/** Singular/plural noun for the count-carrying toasts (the app's `{noun}`
 *  convention — `translate()` does plain substitution, not plural rules). */
function itemNoun(count: number): string {
  return tr(count === 1 ? "ics.item" : "ics.items");
}

function byId<T extends { id: string }>(rows: T[]): Record<string, T> {
  return Object.fromEntries(rows.map((r) => [r.id, r]));
}

function namedById<T extends { id: string; name: string }>(rows: T[]): Record<string, NamedRef> {
  return Object.fromEntries(rows.map((r) => [r.id, { name: r.name }]));
}

function msg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
